import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { StreamTypes } from 'store/player/actions.types';

import { VideoItem } from 'services/data-manager/types';
import useGetPlayerErrorParams from 'services/uav/useGetPlayerErrorParams';
import { logUavAnalytics } from 'services/uav/utils';

import { PlayerError } from 'screens/PlayerModal/types';
import { getErrorDescription } from 'screens/PlayerModal/utils/errors.ts';

type Params = {
    videoItem: VideoItem | undefined;
    playbackType: StreamTypes | undefined;
    videoSessionId: string | undefined;
    onSetReadyToPlay(readyToPlay: boolean): void;
};

function usePlayerError({
    videoItem,
    videoSessionId,
    playbackType,
    onSetReadyToPlay,
}: Params) {
    const { t } = useTranslation();

    const [playerError, setPlayerError] = useState<PlayerError>({});

    const { getPlayerErrorParams } = useGetPlayerErrorParams({
        playbackType,
        videoItem,
    });

    const handleSetPlayerError = useCallback(
        (_error: PlayerError, _videoSessionId?: string | undefined) => {
            const parsedError: PlayerError = {
                ..._error,
                description:
                    _error.description ?? t(getErrorDescription(_error.code)),
            };

            setPlayerError(parsedError);
            onSetReadyToPlay(true);

            logUavAnalytics(
                getPlayerErrorParams(
                    parsedError,
                    _videoSessionId || videoSessionId,
                ),
            );
        },
        [getPlayerErrorParams, onSetReadyToPlay, t, videoSessionId],
    );

    const handleResetUnrecoverableError = useCallback(() => {
        setPlayerError({});
    }, []);

    return {
        playerError,
        onSetPlayerError: handleSetPlayerError,
        onResetPlayerError: handleResetUnrecoverableError,
    };
}

export default usePlayerError;
